import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import type { SkuItem, SkuMappingRule, SkuBundle } from "./skuMapping";
import * as CatalogService from "@/services/catalog.service";
import type {
  CatalogCategory as DbCatalogCategory,
  CatalogProduct as DbCatalogProduct,
  CatalogVariant as DbCatalogVariant,
  CatalogImage as DbCatalogImage,
  CatalogVendor as DbCatalogVendor,
  CatalogSku as DbCatalogSku,
  CatalogSkuMappingRule as DbCatalogSkuMappingRule,
} from "@/types/database";

export interface CatalogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  sortOrder: number;
  active: boolean;
}

export interface CatalogVariant {
  id: string;
  productId: string;
  name: string;
  imageUrl: string;
  referenceImageUrl: string;
  active: boolean;
  sortOrder: number;
  sku: string;
  vendor: string;
  cost: number | null;
  basePrice: number | null;
  description: string;
  items: { sku: string; qty: number }[];
}

export interface CatalogProduct {
  id: string;
  displayName: string;
  categorySlug: string;
  productKind?: "single" | "set" | "bundle";
  materialFilter?: string;
  active: boolean;
  sortOrder: number;
  notes: string;
  pricingImpact: string;
  vendor: string;
  skus: string[];
  variants: CatalogVariant[];
}

function formatPricingImpact(basePrice: number | null): string {
  if (basePrice === null) return "Quote";
  if (basePrice === 0) return "Included";
  return Number.isInteger(basePrice) ? `+$${basePrice}` : `+$${basePrice.toFixed(2)}`;
}

function mapCategory(dbCategory: DbCatalogCategory): CatalogCategory {
  return {
    id: dbCategory.id,
    name: dbCategory.name,
    slug: dbCategory.slug,
    description: dbCategory.description ?? undefined,
    icon: dbCategory.icon ?? undefined,
    sortOrder: dbCategory.sort_order,
    active: dbCategory.is_active,
  };
}

function mapVariant(dbVariant: DbCatalogVariant, images: DbCatalogImage[]): CatalogVariant {
  const primaryImage = images.find((image) => image.is_primary) ?? images[0];
  const secondaryImage = images.find((image) => image.id !== primaryImage?.id) ?? primaryImage;
  const specificationItems =
    dbVariant.specifications &&
    typeof dbVariant.specifications === "object" &&
    "items" in dbVariant.specifications &&
    Array.isArray((dbVariant.specifications as Record<string, unknown>).items)
      ? ((dbVariant.specifications as Record<string, unknown>).items as unknown[])
          .map((item) => {
            if (typeof item === "string") {
              const sku = item.trim();
              return sku ? { sku, qty: 1 } : null;
            }
            if (item && typeof item === "object") {
              const row = item as Record<string, unknown>;
              const sku = String(row.sku ?? "").trim();
              const rawQty = Number(row.qty ?? 1);
              const qty = Number.isFinite(rawQty) && rawQty > 0 ? Math.floor(rawQty) : 1;
              return sku ? { sku, qty } : null;
            }
            return null;
          })
          .filter((item): item is { sku: string; qty: number } => Boolean(item))
      : [];

  return {
    id: dbVariant.id,
    productId: dbVariant.product_id,
    name: dbVariant.name,
    imageUrl: primaryImage?.image_url ?? "",
    referenceImageUrl: secondaryImage?.image_url ?? primaryImage?.thumbnail_url ?? primaryImage?.image_url ?? "",
    active: dbVariant.is_active,
    sortOrder: dbVariant.sort_order,
    sku: dbVariant.sku ?? dbVariant.sku_code ?? "",
    vendor: "",
    cost: dbVariant.cost_adjustment ?? null,
    basePrice: dbVariant.price_adjustment ?? null,
    description: dbVariant.color ?? "",
    items: specificationItems,
  };
}

function mapProduct(
  dbProduct: DbCatalogProduct,
  categoryById: Map<string, CatalogCategory>,
  variants: CatalogVariant[],
  productImages: DbCatalogImage[],
): CatalogProduct {
  const rawType = (dbProduct.product_type ?? "").trim().toLowerCase();
  const isKnownType = rawType === "single" || rawType === "set" || rawType === "bundle";
  const productKind: CatalogProduct["productKind"] = isKnownType ? (rawType as CatalogProduct["productKind"]) : "single";
  const specMaterialFilter =
    dbProduct.specifications &&
    typeof dbProduct.specifications === "object" &&
    "materialFilter" in dbProduct.specifications
      ? String((dbProduct.specifications as Record<string, unknown>).materialFilter ?? "").trim() || undefined
      : undefined;
  const legacyMaterialFilter = !isKnownType && dbProduct.product_type ? dbProduct.product_type : undefined;

  const category = dbProduct.category_id ? categoryById.get(dbProduct.category_id) : undefined;
  const primaryProductImage = productImages.find((image) => image.is_primary) ?? productImages[0];
  const hydratedVariants = variants.length > 0
    ? variants
    : primaryProductImage
      ? [{
          id: `${dbProduct.id}-image`,
          productId: dbProduct.id,
          name: dbProduct.name,
          imageUrl: primaryProductImage.image_url,
          referenceImageUrl: primaryProductImage.thumbnail_url ?? primaryProductImage.image_url,
          active: dbProduct.is_active,
          sortOrder: 1,
          sku: dbProduct.sku ?? "",
          vendor: "",
          cost: dbProduct.cost_price,
          basePrice: dbProduct.base_price,
          description: dbProduct.description ?? "",
          items: [],
        }]
      : [];

  return {
    id: dbProduct.id,
    displayName: dbProduct.name,
    categorySlug: category?.slug ?? "",
    productKind,
    materialFilter: specMaterialFilter ?? legacyMaterialFilter,
    active: dbProduct.is_active,
    sortOrder: category ? category.sortOrder : 999,
    notes: dbProduct.description ?? "",
    pricingImpact: formatPricingImpact(dbProduct.base_price),
    vendor: "",
    skus: [dbProduct.sku, ...hydratedVariants.map((variant) => variant.sku)].filter(
      (sku): sku is string => Boolean(sku),
    ),
    variants: hydratedVariants.sort((a, b) => a.sortOrder - b.sortOrder),
  };
}

function mapSkuCategory(category: string | null): SkuItem["category"] {
  const normalized = (category ?? "").toLowerCase();
  if (normalized === "base" || normalized === "walls" || normalized === "glass" || normalized === "door" || normalized === "fixture" || normalized === "accessory" || normalized === "trim" || normalized === "hardware") {
    return normalized;
  }
  return "accessory";
}

function mapSku(dbSku: DbCatalogSku, vendorNameById: Map<string, string>): SkuItem {
  const primaryVendorName = dbSku.primary_vendor_id
    ? vendorNameById.get(dbSku.primary_vendor_id) || "Catalog Vendor"
    : "Catalog Vendor";
  const alternateVendorName = dbSku.alternate_vendor_id
    ? vendorNameById.get(dbSku.alternate_vendor_id) || undefined
    : undefined;
  return {
    sku: dbSku.sku,
    itemName: dbSku.name,
    category: mapSkuCategory(dbSku.category),
    unitCost: dbSku.unit_cost ?? 0,
    unitPrice: dbSku.unit_price ?? 0,
    active: dbSku.is_active,
    primaryVendor: {
      name: primaryVendorName,
      type: "Primary",
      costTier: 1,
      leadTimeDays: 0,
    },
    alternateVendor: alternateVendorName
      ? {
          name: alternateVendorName,
          type: "Secondary",
          costTier: 2,
          leadTimeDays: 0,
        }
      : undefined,
    externalItemId: dbSku.external_item_id ?? undefined,
  };
}

function mapSkuMappingRule(dbRule: DbCatalogSkuMappingRule): SkuMappingRule {
  return {
    id: dbRule.id,
    name: dbRule.name,
    conditions: dbRule.conditions ?? {},
    skuItems: dbRule.sku_items ?? [],
  };
}

export interface CatalogContextValue {
  categories: CatalogCategory[];
  setCategories: React.Dispatch<React.SetStateAction<CatalogCategory[]>>;
  products: CatalogProduct[];
  setProducts: React.Dispatch<React.SetStateAction<CatalogProduct[]>>;
  getProductsByCategory: (slug: string, materialFilter?: string) => CatalogProduct[];
  getActiveVariants: (productId: string) => CatalogVariant[];
  skuCatalog: SkuItem[];
  setSkuCatalog: React.Dispatch<React.SetStateAction<SkuItem[]>>;
  mappingRules: SkuMappingRule[];
  setMappingRules: React.Dispatch<React.SetStateAction<SkuMappingRule[]>>;
  bundles: SkuBundle[];
  setBundles: React.Dispatch<React.SetStateAction<SkuBundle[]>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  hasLoadedFromDb: boolean;
  setHasLoadedFromDb: React.Dispatch<React.SetStateAction<boolean>>;
  isDbConnected: boolean;
  effectiveCategories: CatalogCategory[];
  dbProducts: CatalogProduct[];
  refreshFromDb: () => Promise<void>;
  getCategoryIdBySlug: (slug: string) => string | null;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [dbProducts, setDbProducts] = useState<CatalogProduct[]>([]);
  const [skuCatalog, setSkuCatalog] = useState<SkuItem[]>([]);
  const [mappingRules, setMappingRules] = useState<SkuMappingRule[]>([]);
  const [bundles, setBundles] = useState<SkuBundle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedFromDb, setHasLoadedFromDb] = useState(false);
  const [isDbConnected, setIsDbConnected] = useState(true);
  const [dbCategories, setDbCategories] = useState<CatalogCategory[]>([]);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  const getProductsByCategory = useCallback(
    (slug: string, materialFilter?: string) =>
      products
        .filter((product) => product.categorySlug === slug && product.active)
        .filter((product) => !materialFilter || !product.materialFilter || product.materialFilter === materialFilter)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName)),
    [products],
  );

  const getActiveVariants = useCallback(
    (productId: string) => {
      const product = products.find((item) => item.id === productId);
      if (!product) return [];
      return product.variants.filter((variant) => variant.active).sort((a, b) => a.sortOrder - b.sortOrder);
    },
    [products],
  );

  const getCategoryIdBySlug = useCallback((slug: string): string | null => {
    const dbCategory = dbCategories.find((category) => category.slug === slug);
    return dbCategory?.id ?? null;
  }, [dbCategories]);

  const refreshFromDb = useCallback(async () => {
    if (deferHeavyProvider) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const categoryRows = await CatalogService.getCategories();
      const mappedCategories = categoryRows.map(mapCategory);
      const categoryById = new Map(mappedCategories.map((category) => [category.id, category]));

      const productRows = await CatalogService.getProducts();
      const [vendorRows, skuRows, skuRuleRows, variantRows, imageRows] = await Promise.all([
        CatalogService.getVendors().catch((error) => {
          console.warn("Failed to load catalog vendors; continuing with placeholder vendor names:", error);
          return [] as DbCatalogVendor[];
        }),
        CatalogService.getSkus().catch((error) => {
          console.warn("Failed to load catalog SKUs; continuing without SKUs:", error);
          return [];
        }),
        CatalogService.getSkuMappingRules().catch((error) => {
          console.warn("Failed to load catalog SKU mapping rules; continuing without mapping rules:", error);
          return [];
        }),
        CatalogService.getVariants().catch((error) => {
          console.warn("Failed to load catalog variants; continuing with product-only catalog:", error);
          return [] as DbCatalogVariant[];
        }),
        CatalogService.getImages().catch((error) => {
          console.warn("Failed to load catalog images; continuing without catalog images:", error);
          return [] as DbCatalogImage[];
        }),
      ]);
      const vendorNameById = new Map(
        vendorRows.map((vendor) => [vendor.id, vendor.name]),
      );
      const variantsByProductId = new Map<string, DbCatalogVariant[]>();
      for (const variant of variantRows) {
        const list = variantsByProductId.get(variant.product_id) ?? [];
        list.push(variant);
        variantsByProductId.set(variant.product_id, list);
      }

      const imagesByProductId = new Map<string, DbCatalogImage[]>();
      const imagesByVariantId = new Map<string, DbCatalogImage[]>();
      for (const image of imageRows) {
        if (image.product_id) {
          const productImages = imagesByProductId.get(image.product_id) ?? [];
          productImages.push(image);
          imagesByProductId.set(image.product_id, productImages);
        }
        if (image.variant_id) {
          const variantImages = imagesByVariantId.get(image.variant_id) ?? [];
          variantImages.push(image);
          imagesByVariantId.set(image.variant_id, variantImages);
        }
      }

      const mappedProducts = productRows.map((productRow) => {
        const productVariants = variantsByProductId.get(productRow.id) ?? [];
        const productImages = imagesByProductId.get(productRow.id) ?? [];
        const mappedVariants = productVariants.map((variantRow) =>
          mapVariant(variantRow, imagesByVariantId.get(variantRow.id) ?? []),
        );

        return mapProduct(productRow, categoryById, mappedVariants, productImages);
      });

      setDbCategories(mappedCategories);
      setCategories(mappedCategories);
      setDbProducts(mappedProducts);
      setProducts(mappedProducts);
      setSkuCatalog(skuRows.map((row) => mapSku(row, vendorNameById)));
      setMappingRules(skuRuleRows.map(mapSkuMappingRule));
      setBundles([]);
      setIsDbConnected(true);
    } catch (error) {
      console.error("Failed to load catalog from DB:", error);
      // Preserve last-known catalog state so feature UIs (e.g. AppointmentFlow) remain usable
      // during transient DB/RLS issues. We still mark the provider as disconnected.
      setIsDbConnected(false);
    } finally {
      setHasLoadedFromDb(true);
      setIsLoading(false);
    }
  }, [deferHeavyProvider]);

  useEffect(() => {
    if (deferHeavyProvider) {
      setIsLoading(false);
      return;
    }
    refreshFromDb();
  }, [deferHeavyProvider, refreshFromDb]);

  return (
    <CatalogContext.Provider value={{
      categories,
      setCategories,
      products,
      setProducts,
      getProductsByCategory,
      getActiveVariants,
      skuCatalog,
      setSkuCatalog,
      mappingRules,
      setMappingRules,
      bundles,
      setBundles,
      isLoading,
      setIsLoading,
      hasLoadedFromDb,
      setHasLoadedFromDb,
      isDbConnected,
      effectiveCategories: categories,
      dbProducts,
      refreshFromDb,
      getCategoryIdBySlug,
    }}
    >
      {children}
    </CatalogContext.Provider>
  );
}

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalog must be used within CatalogProvider");
  return ctx;
}

export type ProductOptionCategory =
  | "wall_system" | "base" | "grab_bar" | "glass" | "door" | "fixture" | "add_on" | "enclosure";

export interface ProductOption {
  id: string;
  displayName: string;
  category: ProductOptionCategory;
  materialFilter?: string;
  active: boolean;
  sortOrder: number;
  notes: string;
  skuPlaceholder: string;
  pricingImpact: string;
}

export const CATEGORY_LABELS: Record<ProductOptionCategory, string> = {
  wall_system: "Wall Systems",
  base: "Base Systems",
  grab_bar: "Grab Bars",
  glass: "Glass Options",
  door: "Door Types",
  fixture: "Fixtures",
  add_on: "Add-ons",
  enclosure: "Enclosures",
};

export const ALL_CATEGORIES: ProductOptionCategory[] = [
  "wall_system", "base", "fixture", "enclosure", "add_on", "grab_bar", "glass", "door",
];

export function useProductOptions() {
  const { products, setProducts, getProductsByCategory } = useCatalog();

  const options: ProductOption[] = products.map((product) => ({
    id: product.id,
    displayName: product.displayName,
    category: product.categorySlug as ProductOptionCategory,
    materialFilter: product.materialFilter,
    active: product.active,
    sortOrder: product.sortOrder,
    notes: product.notes,
    skuPlaceholder: product.skus[0] || "",
    pricingImpact: product.pricingImpact,
  }));

  const setOptions: React.Dispatch<React.SetStateAction<ProductOption[]>> = (action) => {
    setProducts((prev) => {
      const newOptions = typeof action === "function"
        ? action(prev.map((product) => ({
            id: product.id,
            displayName: product.displayName,
            category: product.categorySlug as ProductOptionCategory,
            materialFilter: product.materialFilter,
            active: product.active,
            sortOrder: product.sortOrder,
            notes: product.notes,
            skuPlaceholder: product.skus[0] || "",
            pricingImpact: product.pricingImpact,
          })))
        : action;

      return newOptions.map((option) => {
        const existing = prev.find((product) => product.id === option.id);
        return {
          id: option.id,
          displayName: option.displayName,
          categorySlug: option.category,
          materialFilter: option.materialFilter,
          active: option.active,
          sortOrder: option.sortOrder,
          notes: option.notes,
          pricingImpact: option.pricingImpact,
          vendor: existing?.vendor || "",
          skus: existing?.skus || [option.skuPlaceholder].filter(Boolean),
          variants: existing?.variants || [],
        };
      });
    });
  };

  const getByCategory = (category: ProductOptionCategory, materialFilter?: string) =>
    getProductsByCategory(category, materialFilter).map((product) => ({
      id: product.id,
      displayName: product.displayName,
      category: product.categorySlug as ProductOptionCategory,
      materialFilter: product.materialFilter,
      active: product.active,
      sortOrder: product.sortOrder,
      notes: product.notes,
      skuPlaceholder: product.skus[0] || "",
      pricingImpact: product.pricingImpact,
    }));

  return { options, setOptions, getByCategory };
}
